import { ActionStageMap } from '../components/WorkflowTrackingTab';

/**
 * One map per entity type whose controller logs semantic action names
 * (not a real status pair) to the audit log. Every action name here was
 * confirmed by reading the actual controller source, not guessed - see
 * WorkflowTrackingTab.tsx for the entities (vacancy, grant) that don't
 * need a map at all because they log a genuine status diff instead.
 */

export const WORKFORCE_REQUEST_STAGES: ActionStageMap = {
  submitted: { from: 'Draft', to: 'Pending approval', task: 'Review request' },
  request_approved: { from: 'Pending approval', to: 'Approved', task: 'Create vacancy' },
  request_rejected: { from: 'Pending approval', to: 'Rejected', task: 'None' },
  returned_for_redraft: { from: 'Pending approval', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  resubmitted: { from: 'Returned for redraft', to: 'Pending approval', task: 'Review request' },
};

export const LEAVE_REQUEST_STAGES: ActionStageMap = {
  applied: { from: 'None', to: 'Pending supervisor review', task: 'Review request' },
  supervisor_approved: { from: 'Pending supervisor review', to: 'Pending HR confirmation', task: 'Confirm leave' },
  supervisor_rejected: { from: 'Pending supervisor review', to: 'Rejected', task: 'None' },
  returned_by_supervisor: { from: 'Pending supervisor review', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  returned_by_hr: { from: 'Pending HR confirmation', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  resubmitted: { from: 'Returned for redraft', to: 'Pending supervisor review', task: 'Review request' },
  hr_approved: { from: 'Pending HR confirmation', to: 'Confirmed', task: 'None' },
  hr_rejected: { from: 'Pending HR confirmation', to: 'Rejected', task: 'None' },
};

export const TRAINING_REQUEST_STAGES: ActionStageMap = {
  requested: { from: 'None', to: 'Pending supervisor review', task: 'Review request' },
  supervisor_approved: { from: 'Pending supervisor review', to: 'Pending HR review', task: 'Review request' },
  supervisor_rejected: { from: 'Pending supervisor review', to: 'Rejected', task: 'None' },
  returned_by_supervisor: { from: 'Pending supervisor review', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  returned_by_hr: { from: 'Pending HR review', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  resubmitted: { from: 'Returned for redraft', to: 'Pending supervisor review', task: 'Review request' },
  hr_approved: { from: 'Pending HR review', to: 'Approved', task: 'Attend training' },
  hr_rejected: { from: 'Pending HR review', to: 'Rejected', task: 'None' },
  completed: { from: 'Approved', to: 'Completed', task: 'None' },
};

export const STAFF_MOVEMENT_STAGES: ActionStageMap = {
  submitted: { from: 'Draft', to: 'Submitted', task: 'Move to HR review' },
  moved_to_review: { from: 'Submitted', to: 'Under review', task: 'Approve or reject' },
  approved: { from: 'Under review', to: 'Approved', task: 'Implement on effective date' },
  rejected: { from: 'Under review', to: 'Rejected', task: 'None' },
  returned_for_redraft: { from: 'Under review', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  resubmitted: { from: 'Returned for redraft', to: 'Submitted', task: 'Move to HR review' },
  implemented: { from: 'Approved', to: 'Implemented', task: 'None' },
  cancelled: { from: 'Draft', to: 'Cancelled', task: 'None' },
};

export const HR_REQUEST_STAGES: ActionStageMap = {
  submitted: { from: 'Draft', to: 'Submitted', task: 'Review request' },
  profile_update_submitted: { from: 'Draft', to: 'Submitted', task: 'Review proposed changes' },
  approved: { from: 'Submitted', to: 'Approved', task: 'Complete request' },
  returned: { from: 'Submitted', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  returned_for_redraft: { from: 'Submitted', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  resubmitted: { from: 'Returned for redraft', to: 'Submitted', task: 'Review request' },
  completed: { from: 'Approved', to: 'Completed', task: 'None' },
};

export const PROBATION_STAGES: ActionStageMap = {
  supervisor_recommendation_submitted: { from: 'In progress', to: 'Awaiting HR decision', task: 'Record HR decision' },
};

export const JOURNAL_ENTRY_STAGES: ActionStageMap = {
  submitted: { from: 'Draft', to: 'Submitted', task: 'Move to review' },
  moved_to_review: { from: 'Submitted', to: 'Under review', task: 'Approve or reject' },
  approved: { from: 'Under review', to: 'Approved', task: 'Post to ledger' },
  rejected: { from: 'Under review', to: 'Rejected', task: 'None' },
  returned_for_redraft: { from: 'Under review', to: 'Returned for redraft', task: 'Address feedback and resubmit' },
  resubmitted: { from: 'Returned for redraft', to: 'Submitted', task: 'Move to review' },
  posted: { from: 'Approved', to: 'Posted', task: 'None' },
  reversed: { from: 'Posted', to: 'Reversed', task: 'None' },
};

export const EXPENSE_CLAIM_STAGES: ActionStageMap = {
  submitted: { from: 'Draft', to: 'Submitted', task: 'Approve or reject' },
  approved: { from: 'Submitted', to: 'Approved', task: 'Record payment' },
  rejected: { from: 'Submitted', to: 'Rejected', task: 'None' },
};

export const BUDGET_STAGES: ActionStageMap = {
  approved: { from: 'Draft', to: 'Approved', task: 'Track against actuals' },
  closed: { from: 'Approved', to: 'Closed', task: 'None' },
};

export const INCOME_RECORD_STAGES: ActionStageMap = {
  submitted: { from: 'Draft', to: 'Submitted', task: 'Approve or reject' },
  approved: { from: 'Submitted', to: 'Approved', task: 'None' },
  rejected: { from: 'Submitted', to: 'Rejected', task: 'None' },
};

export const RECEIVABLE_STAGES: ActionStageMap = {
  collection_recorded: { from: 'Outstanding', to: 'Collected', task: 'None' },
};

export const PAYABLE_STAGES: ActionStageMap = {
  payment_recorded: { from: 'Outstanding', to: 'Paid', task: 'None' },
};

export const CONTRACT_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Draft', task: 'None' },
  // "Draft/Returned" rather than a single hardcoded status - update()
  // is genuinely allowed from either, and this map has no access to
  // which one a given entry actually came from.
  updated: { from: 'Draft/Returned', to: 'Draft/Returned', task: 'Edited' },
  approval_requested: { from: 'HR Review', to: 'Pending Approval', task: 'None' },
  contract_approved: { from: 'Pending Approval', to: 'Approved', task: 'None' },
  contract_rejected: { from: 'Pending Approval', to: 'Rejected', task: 'None' },
  returned_for_redraft: { from: 'HR Review', to: 'Returned for Redraft', task: 'None' },
  resubmitted: { from: 'Returned for Redraft', to: 'HR Review', task: 'None' },
  // submitted_for_review / signed / finalised are deliberately NOT
  // mapped here - their audit entries always carry a genuine status
  // change in old_values/new_values, so WorkflowTrackingTab's
  // automatic status-diff resolution always intercepts them first;
  // an entry here would be silent dead code. statusLabels (passed by
  // the caller) is what actually controls their display text.
};

/**
 * Delegation, Department, Location, Position, Employee - these are
 * simple CRUD records, not sequential approval workflows (no
 * draft->review->approved chain exists for a department). Per
 * explicit direction, given the boxed visualization anyway for visual
 * consistency across the whole app - each real logged action maps to
 * a genuine before/after state, just a simpler one than an approval
 * entity's multi-step sequence.
 */
export const DELEGATION_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Active', task: 'None' },
  cancelled: { from: 'Active', to: 'Cancelled', task: 'None' },
};

export const DEPARTMENT_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Active', task: 'None' },
  updated: { from: 'Active', to: 'Active', task: 'Edited' },
  deactivated: { from: 'Active', to: 'Inactive', task: 'None' },
};

export const LOCATION_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Active', task: 'None' },
  updated: { from: 'Active', to: 'Active', task: 'Edited' },
  deactivated: { from: 'Active', to: 'Inactive', task: 'None' },
};

export const POSITION_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Active', task: 'None' },
  updated: { from: 'Active', to: 'Active', task: 'Edited' },
  deactivated: { from: 'Active', to: 'Inactive', task: 'None' },
};

export const EMPLOYEE_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Active', task: 'None' },
  updated: { from: 'Active', to: 'Active', task: 'Profile updated' },
  self_profile_updated: { from: 'Active', to: 'Active', task: 'Self-service update' },
  photo_updated: { from: 'Active', to: 'Active', task: 'Photo updated' },
  salary_updated: { from: 'Active', to: 'Active', task: 'Salary updated' },
  staff_id_generated: { from: 'Active', to: 'Active', task: 'Staff ID assigned' },
  deleted: { from: 'Active', to: 'Deleted', task: 'None' },
};

/**
 * Partner Due Diligence Case - rides on the same shared WorkflowState
 * machine as every other approvable module. Only 'created' needs an
 * explicit entry here (no genuine before/after status pair exists at
 * creation); every other real transition (submit, review, return,
 * request-info, resubmit, decide) already logs a genuine
 * old_values.status/new_values.status pair, so WorkflowTrackingTab's
 * automatic status-diff resolution handles them directly - an entry
 * here for those would be silent dead code, same reasoning as
 * CONTRACT_STAGES above.
 */
export const PARTNER_DD_CASE_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Draft', task: 'None' },
};

/**
 * Travel Request - same reasoning as PARTNER_DD_CASE_STAGES above.
 * Every real transition (submit, each approval step, decision,
 * cancel) now logs a genuine old_values.status/new_values.status pair
 * - only 'created' needs an explicit entry here.
 */
export const TRAVEL_REQUEST_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Draft', task: 'None' },
};

/** Logistics Request - identical reasoning to TRAVEL_REQUEST_STAGES above. */
export const LOGISTICS_REQUEST_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Draft', task: 'None' },
};

/**
 * Assets have no submit/approve workflow at all - a register entry
 * created directly, then tracked through assignment, movement, and
 * disposal as separate sub-entities. This stage map exists purely so
 * WorkflowTrackingTab can show a real created-by/when entry; every
 * other audit action (assigned, assignment_returned,
 * movement_dispatched, movement_received) already carries a genuine
 * status pair in old_values/new_values and resolves automatically.
 */
export const ASSET_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Available', task: 'None' },
};

/** Vehicles - same reasoning as ASSET_STAGES: a register entry created directly, tracked through dispatch/fuel/maintenance as separate sub-entities. */
export const VEHICLE_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Available', task: 'None' },
};

/** Vehicle Request - a real submit/approve/reject workflow, same reasoning as TRAVEL_REQUEST_STAGES/LOGISTICS_REQUEST_STAGES. */
export const VEHICLE_REQUEST_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Draft', task: 'None' },
};

/** Stock Request - a real submit/issue workflow. */
export const STOCK_REQUEST_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Pending', task: 'None' },
};

/** Goods Receipt - a real draft/submit/verify(post) workflow. */
export const GOODS_RECEIPT_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Draft', task: 'None' },
};

/** Stock Adjustment - a real draft/submit/approve-or-reject workflow with genuine segregation of duties. */
export const STOCK_ADJUSTMENT_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Draft', task: 'None' },
};

/** Stock Count - a real planned/in-progress/submit/review/approve/close workflow. */
export const STOCK_COUNT_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Planned', task: 'None' },
};

/** Waybill - a real preparing/dispatched/delivered workflow with genuine proof-of-delivery capture. */
export const WAYBILL_STAGES: ActionStageMap = {
  created: { from: 'None', to: 'Preparing', task: 'None' },
};

